import { Suspense, lazy } from "react";
import TopAndRightNavBar from "../components/navBars/TopAndRightNavBar";
import RouteFallback from "../components/simpleComponents/RouteFallback";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
    AllCasesScreenName,
    AllCasesTypeScreenName,
    AllClientsScreenName,
    AllMangerScreenName,
    CalendarScreenName,
    DailyAgendaScreenName,
    EvidenceDocumentsScreenName,
    MainScreenName,
    MyCasesScreenName,
    PlanUsageScreenName,
    PlansPricingScreenName,
    PlatformSettingsScreenName,
    OfficeIntegrationsScreenName,
    FirmStaffRolesScreenName,
    RemindersScreenName,
    AdminSupportScreenName,
    SigningManagerScreenName,
    SigningSpotsPreviewScreenName,
    TaggedCasesScreenName,
    uploadFileForSigningScreenName,
    LoginScreenName,
    LoginStackName,
    AdminStackName,
} from "./screenPaths";
import { useCalendarModuleEnabled } from "../services/firmSettings";
import BillingLockedScreen from "../components/billing/BillingLockedScreen";
import { useBillingLock } from "../providers/BillingLockProvider";
import AdminRouteGuard from "./AdminRouteGuard";
import NoPermissionsScreen, { NoPermissionsScreenName } from "../screens/noPermissions/NoPermissionsScreen";
import { useOfficeLogoNavigate } from "./useOfficeLogoNavigate";

const MainScreen = lazy(() => import("../screens/mainScreen/MainScreen"));
const TaggedCasesScreen = lazy(() => import("../screens/taggedCasesScreen/TaggedCasesScreen"));
const AllCasesScreen = lazy(() => import("../screens/allCasesScreen/AllCasesScreen"));
const AllClientsScreen = lazy(() => import("../screens/allClientsScreen/AllClientsScreen"));
const MyCasesScreen = lazy(() => import("../screens/myCasesScreen/MyCasesScreen"));
const AllMangerScreen = lazy(() => import("../screens/allMangerScreen/AllMangerScreen"));
const AllCasesTypeScreen = lazy(() => import("../screens/allCasesTypeScreen/AllCasesTypeScreen"));
const RemindersScreen = lazy(() => import("../screens/remindersScreen/RemindersScreen"));
const AdminSupportScreen = lazy(() => import("../screens/admin/support/AdminSupportScreen"));
const SigningManagerScreen = lazy(() => import("../screens/signingScreen/SigningManagerScreen"));
const SigningSpotsPreviewScreen = lazy(() => import("../screens/signingScreen/SigningSpotsPreviewScreen"));
const UploadFileForSigningScreen = lazy(() => import("../screens/signingScreen/UploadFileForSigningScreen"));
const EvidenceDocumentsScreen = lazy(() => import("../screens/evidenceDocuments/EvidenceDocumentsScreen"));
const PlanUsageScreen = lazy(() => import("../screens/billingScreen/PlanUsageScreen"));
const PlansPricingScreen = lazy(() => import("../screens/billingScreen/PlansPricingScreen"));
const PlatformSettingsScreen = lazy(() => import("../screens/platformSettingsScreen/PlatformSettingsScreen"));
const OfficeIntegrations = lazy(() => import('../screens/officeIntegrations/OfficeIntegrationsScreen'));
const FirmStaffRolesScreen = lazy(() => import("../screens/firmStaffRoles/FirmStaffRolesScreen"));
const CalendarScreen = lazy(() => import("../screens/calendarScreen/CalendarScreen"));
const DailyAgendaScreen = lazy(() => import("../screens/calendarScreen/DailyAgendaScreen"));

export { AdminStackName };

function toRelativePath(pathname) {
    const p = String(pathname || "");
    return p.startsWith("/") ? p.slice(1) : p;
}

function AdminStack() {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const calendarEnabled = useCalendarModuleEnabled();
    const location = useLocation();
    const { locked, loaded, canManageBilling } = useBillingLock();
    const isPlatformAdmin = canManageBilling === true;
    const logoNavigate = useOfficeLogoNavigate();
    if (!token) return <Navigate to={LoginStackName + LoginScreenName} replace />;

    const onBillingRoute = /PlanUsage|PlansPricing/i.test(location.pathname || "");
    if (loaded && locked && !(isPlatformAdmin && onBillingRoute)) {
        if (isPlatformAdmin) {
            return <Navigate to={`${AdminStackName}${PlanUsageScreenName}`} replace />;
        }
        return <BillingLockedScreen />;
    }

    return (
        <TopAndRightNavBar LogoNavigate={logoNavigate}>
            <AdminRouteGuard>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path={toRelativePath(NoPermissionsScreenName)} element={<NoPermissionsScreen />} />
                    <Route path={toRelativePath(MainScreenName)} element={<MainScreen />} />
                    <Route path={toRelativePath(TaggedCasesScreenName)} element={<TaggedCasesScreen />} />
                    <Route path={toRelativePath(AllCasesScreenName)} element={<AllCasesScreen />} />
                    <Route path={toRelativePath(AllClientsScreenName)} element={<AllClientsScreen />} />
                    <Route path={toRelativePath(MyCasesScreenName)} element={<MyCasesScreen />} />
                    <Route path={toRelativePath(AllMangerScreenName)} element={<AllMangerScreen />} />
                    <Route path={toRelativePath(AllCasesTypeScreenName)} element={<AllCasesTypeScreen />} />
                    <Route path={toRelativePath(SigningManagerScreenName)} element={<SigningManagerScreen />} />
                    <Route path={toRelativePath(SigningSpotsPreviewScreenName)} element={<SigningSpotsPreviewScreen />} />
                    <Route path={toRelativePath(EvidenceDocumentsScreenName)} element={<EvidenceDocumentsScreen />} />
                    <Route path={toRelativePath(PlanUsageScreenName)} element={<PlanUsageScreen />} />
                    <Route path={toRelativePath(PlansPricingScreenName)} element={<PlansPricingScreen />} />
                    <Route path={toRelativePath(uploadFileForSigningScreenName)} element={<UploadFileForSigningScreen />} />
                    <Route path={toRelativePath(RemindersScreenName)} element={<RemindersScreen />} />
                    <Route path={toRelativePath(AdminSupportScreenName)} element={<AdminSupportScreen />} />
                    <Route path={toRelativePath(PlatformSettingsScreenName)} element={<PlatformSettingsScreen />} />
                    <Route path={toRelativePath(OfficeIntegrationsScreenName)} element={<OfficeIntegrations />} />
                    <Route path={toRelativePath(FirmStaffRolesScreenName)} element={<FirmStaffRolesScreen />} />
                    {calendarEnabled && (
                        <>
                            <Route path={toRelativePath(CalendarScreenName)} element={<CalendarScreen />} />
                            <Route path={toRelativePath(DailyAgendaScreenName)} element={<DailyAgendaScreen />} />
                        </>
                    )}
                </Routes>
            </Suspense>
            </AdminRouteGuard>
        </TopAndRightNavBar>
    );
}

export default AdminStack;
